/**
 * SafeLink — href sanitization and target=_blank hardening.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { safeRelativePath } from "../utils/safeRelativePath.js";

export interface SafeLinkProps extends Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "href" | "rel" | "target"
> {
  readonly href?: string | null;
  readonly openExternalInNewTab?: boolean;
  readonly unsafeFallback?: React.ReactNode;
}

function isFragment(href: string): boolean {
  return /^#[A-Za-z0-9_.:-]*$/.test(href);
}

export function sanitizeHref(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  if (isFragment(trimmed)) return trimmed;

  try {
    const parsed = new URL(trimmed);
    if (
      parsed.protocol === "http:" ||
      parsed.protocol === "https:" ||
      parsed.protocol === "mailto:"
    ) {
      return trimmed;
    }
    return null;
  } catch {
    return safeRelativePath(trimmed);
  }
}

export function isExternalHref(href: string): boolean {
  try {
    const parsed = new URL(href);
    return (
      parsed.protocol === "http:" || parsed.protocol === "https:" || parsed.protocol === "mailto:"
    );
  } catch {
    return false;
  }
}

export const SafeLink = React.forwardRef<HTMLAnchorElement, SafeLinkProps>(function SafeLink(
  { href, openExternalInNewTab = true, unsafeFallback, children, ...props }: SafeLinkProps,
  ref,
): React.JSX.Element {
  const safeHref = sanitizeHref(href);
  if (safeHref === null) {
    return <span>{unsafeFallback ?? children}</span>;
  }

  const external = isExternalHref(safeHref);
  const target = external && openExternalInNewTab ? "_blank" : undefined;
  const rel = target === "_blank" ? "noopener noreferrer" : undefined;

  return (
    <a ref={ref} href={safeHref} target={target} rel={rel} {...props}>
      {children}
    </a>
  );
});
